/**
 * /admin/ops/export — SCRUM-329.
 *
 * Decisión documentada: esta pantalla se conserva, con alcance acotado a
 * pedidos (soporte/debugging/migración puntual) — sin herramienta
 * equivalente en el producto merchant. El export de tenant completo
 * (catálogo, usuarios, config, suscripción) vive en Backup & Restore
 * (SCRUM-314/323, /admin/ops/backup) — no se duplica acá.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { OpsSubnav } from "@/components/OpsSubnav";
import { OpsTenantBar, opsAuthHeaders, useOpsTenantId } from "@/components/admin/OpsTenantBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface ExportHistoryRow {
  id: string;
  type: string;
  createdAt: string;
  actor: string | null;
  detail: Record<string, unknown>;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });
}

export const Route = createFileRoute("/admin/ops/export")({
  component: ExportPage,
  head: () => ({ meta: [{ title: "Export — Operaciones" }] }),
});

function ExportPage() {
  const [tenantId, setTenantId] = useOpsTenantId();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [count, setCount] = useState<number | null>(null);
  const [checkingCount, setCheckingCount] = useState(false);
  const [history, setHistory] = useState<ExportHistoryRow[]>([]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch("/api/ops/export/orders?resource=history", {
        credentials: "include",
        headers: opsAuthHeaders(tenantId),
      });
      if (!res.ok) return;
      const data = (await res.json()) as { history?: ExportHistoryRow[] };
      setHistory(data.history ?? []);
    } catch {
      /* best-effort */
    }
  }, [tenantId]);

  useEffect(() => {
    void loadHistory();
    setCount(null);
  }, [loadHistory]);

  const checkCount = async () => {
    setCheckingCount(true);
    try {
      const params = new URLSearchParams({ count: "1" });
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const res = await fetch(`/api/ops/export/orders?${params}`, { credentials: "include", headers: opsAuthHeaders(tenantId) });
      if (!res.ok) {
        toast.error("No se pudo calcular la cantidad de registros");
        return;
      }
      const data = (await res.json()) as { count: number; limit: number };
      setCount(data.count);
      if (data.count > data.limit) {
        toast.warning(`Hay ${data.count} pedidos — el export se corta en ${data.limit} filas.`);
      }
    } finally {
      setCheckingCount(false);
    }
  };

  const download = async (format: "csv" | "json") => {
    const params = new URLSearchParams({ format });
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    try {
      const res = await fetch(`/api/ops/export/orders?${params}`, { credentials: "include", headers: opsAuthHeaders(tenantId) });
      if (!res.ok) {
        toast.error("No se pudo exportar");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pedidos-${tenantId}.${format === "json" ? "json" : "csv"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export listo");
      await loadHistory();
    } catch {
      toast.error("Error de red");
    }
  };

  return (
    <div>
      <h1 className="px-6 pt-5 pb-2 text-lg font-bold text-foreground">Export</h1>
      <div className="p-4 sm:p-6">
        <OpsSubnav hint="Exportación de pedidos del workspace — soporte, debugging o migración puntual." />
        <OpsTenantBar tenantId={tenantId} onTenantChange={setTenantId} />

        <p className="mb-4 max-w-lg text-xs text-muted-foreground">
          ¿Buscás exportar el catálogo, usuarios o configuración de un tenant completo? Eso vive en{" "}
          <Link to="/admin/ops/backup" search={{} as never} className="text-primary hover:underline">
            Backup &amp; Restore
          </Link>
          . Esta pantalla es solo para pedidos.
        </p>

        <div className="glass-card mb-4 max-w-lg space-y-4 rounded-xl p-4">
          <p className="text-sm text-muted-foreground">Descargá pedidos en CSV o JSON (hasta 5000 filas).</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Desde</Label>
              <Input className="mt-1" type="date" onChange={(e) => setFrom(e.target.value ? `${e.target.value}T00:00:00Z` : "")} />
            </div>
            <div>
              <Label className="text-xs">Hasta</Label>
              <Input className="mt-1" type="date" onChange={(e) => setTo(e.target.value ? `${e.target.value}T23:59:59Z` : "")} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => void checkCount()} disabled={checkingCount}>
              {checkingCount ? "Calculando…" : "Previsualizar cantidad"}
            </Button>
            {count != null && <span className="text-xs text-muted-foreground">{count} pedidos en el rango</span>}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void download("csv")}>
              CSV pedidos
            </Button>
            <Button size="sm" variant="outline" onClick={() => void download("json")}>
              JSON pedidos
            </Button>
          </div>
        </div>

        <section className="max-w-lg">
          <h2 className="mb-2 text-sm font-medium">Historial de exportaciones ({history.length})</h2>
          {history.length === 0 ? (
            <p className="text-xs italic text-muted-foreground">Sin exportaciones registradas todavía.</p>
          ) : (
            <div className="space-y-1">
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between rounded-md border px-3 py-2 text-xs">
                  <div>
                    <span className="font-medium">{h.type}</span>{" "}
                    <span className="text-muted-foreground">{h.actor ?? "—"}</span>
                    {typeof h.detail.rowCount === "number" && (
                      <span className="text-muted-foreground"> · {h.detail.rowCount} filas</span>
                    )}
                  </div>
                  <span className="text-muted-foreground">{fmtDate(h.createdAt)}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
